<template>
  <v-card class="pa-3">
    <!-- Overlay to close dropdown when clicking outside -->
    <div
      v-if="activeFilterColumn"
      class="dropdown-overlay"
      @click="closeDropdown"
    ></div>

    <v-card-title
      class="headline pr-0 pt-0 pb-2 d-flex align-center justify-space-between"
    >
      <span
        >Dashboard Summary

        <v-btn icon small @click="loadData" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </span>
    </v-card-title>

    <div v-if="activeFilterLabel" class="mb-2">
      <v-chip color="primary" text-color="white" small class="mr-2 mb-1">
        <v-icon left small>mdi-filter</v-icon>
        {{ activeFilterLabel }}
        <v-icon
          small
          class="ml-2"
          color="white"
          @click="clearFilter"
          style="cursor: pointer; opacity: 0.8"
          @mouseenter="$event.target.style.opacity = 1"
          @mouseleave="$event.target.style.opacity = 0.8"
        >
          mdi-close-circle
        </v-icon>
      </v-chip>
    </div>

    <div v-if="!loading" dense class="summary-table">
      <thead>
        <tr>
          <th>
            <v-select
              v-model="selectedTechnicianNames"
              :items="sortedTechnicians"
              item-text="technicianName"
              item-value="technicianName"
              :label="
                `${
                  selectedTechnicians &&
                  selectedTechnicians.length == sortedTechnicians.length
                    ? 'All Technicians'
                    : selectedTechnicians.length == 0
                    ? `Technicians (${selectedTechnicians.length})`
                    : ``
                }`
              "
              multiple
              dense
              outlined
              hide-details
              @change="onTechnicianSelected"
            >
              <template v-slot:prepend-item>
                <div @click="toggleSelectAll" class="select-all-item">
                  <v-icon small>{{ selectAllIcon }}</v-icon>
                  <span class="ml-2">Select All</span>
                </div>
                <v-divider class="mt-2 mb-2"></v-divider>
              </template>
              <template v-slot:selection="{ index }">
                <span v-if="index === 0">{{
                  selectedTechnicians &&
                  selectedTechnicians.length != sortedTechnicians.length
                    ? `Technicians (${selectedTechnicians.length || 0})`
                    : `All Technicians`
                }}</span>
              </template>
            </v-select>
          </th>
          <!-- Columns with filter dropdown -->
          <th
            v-for="col in ['open', 'onHold', 'closed', 'total']"
            :key="col"
            class="text-center cursor-pointer position-relative"
          >
            <div
              @click="toggleFilterDropdown(col)"
              class="d-flex align-center justify-center"
            >
              <span>{{ columnLabels[col] }}</span>
              <v-icon
                small
                class="ml-1"
                :color="currentFilter.column === col ? 'primary' : ''"
              >
                {{
                  activeFilterColumn === col
                    ? "mdi-chevron-up"
                    : "mdi-chevron-down"
                }}
              </v-icon>
            </div>
            <div v-if="activeFilterColumn === col" class="date-dropdown-menu">
              <v-radio-group
                v-model="currentFilter.dateType"
                row
                dense
                hide-details
                class="mt-0 mb-2"
                @change="onDateTypeChange"
              >
                <v-radio label="Create Date" value="createDate"></v-radio>
                <v-radio label="Due Date" value="dueDate"></v-radio>
              </v-radio-group>
              <v-select
                v-if="currentFilter.dateType"
                v-model="currentFilter.dateSelection"
                @change="onDateSelectionChanged"
                :items="dateSelectionItems"
                label="Select Date"
                outlined
                dense
                hide-details
                style="min-width: 150px"
              ></v-select>
              <div
                v-if="
                  currentFilter.dateType &&
                    currentFilter.dateSelection === 'select range'
                "
                class="mt-2"
              >
                <v-text-field
                  v-model="currentFilter.fromDate"
                  type="date"
                  label="From Date"
                  prepend-icon="mdi-calendar"
                  outlined
                  dense
                  hide-details
                  class="mb-2"
                ></v-text-field>
                <v-text-field
                  v-model="currentFilter.toDate"
                  type="date"
                  label="To Date"
                  prepend-icon="mdi-calendar"
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="tech in selectedTechnicians" :key="tech.technicianName">
          <td>
            {{ tech.technicianName || "Unassigned" }}
          </td>
          <td class="text-center stat-cell">
            <a
              @click="
                openTechnicianRequests(tech.technicianName, 'Open', false)
              "
              class="stat-number"
            >
              {{ tech.open > 0 ? formatNumberWithCommas(tech.open) : "-" }}
            </a>

            <div class="overdue-number">
              <a
                @click="
                  openTechnicianRequests(tech.technicianName, 'Open', true)
                "
                class="overdue-number"
              >
                {{
                  tech.overdueOpen > 0
                    ? formatNumberWithCommas(tech.overdueOpen)
                    : "-"
                }}
              </a>
            </div>
          </td>
          <td class="text-center stat-cell">
            <div class="stat-number">
              <a
                @click="
                  openTechnicianRequests(tech.technicianName, 'On Hold', false)
                "
                class="stat-number"
              >
                {{
                  tech.onHold > 0 ? formatNumberWithCommas(tech.onHold) : "-"
                }}
              </a>
            </div>
            <div class="overdue-number">
              <a
                @click="
                  openTechnicianRequests(tech.technicianName, 'On Hold', true)
                "
                class="overdue-number"
              >
                {{
                  tech.overdueOnHold > 0
                    ? formatNumberWithCommas(tech.overdueOnHold)
                    : "-"
                }}
              </a>
            </div>
          </td>
          <td class="text-center stat-cell">
            <a
              @click="
                openTechnicianRequests(tech.technicianName, 'Closed', false)
              "
              class="stat-number"
            >
              {{ tech.closed > 0 ? formatNumberWithCommas(tech.closed) : "-" }}
            </a>
          </td>
          <td class="text-center stat-cell">
            <div class="stat-number">
              <a
                @click="
                  openTechnicianRequests(tech.technicianName, null, false)
                "
                class="stat-number"
              >
                {{ tech.total > 0 ? formatNumberWithCommas(tech.total) : "-" }}
              </a>
            </div>
            <div class="overdue-number">
              <a
                @click="openTechnicianRequests(tech.technicianName, null, true)"
                class="overdue-number"
              >
                {{
                  tech.overdueTotal > 0
                    ? formatNumberWithCommas(tech.overdueTotal)
                    : "-"
                }}
              </a>
            </div>
          </td>
        </tr>
        <tr
          v-if="
            (selectedTechnicians.length > 0 &&
              selectedTechnicians.length != sortedTechnicians.length) ||
              this.appliedFilter
          "
          class="total-row font-weight-bold"
        >
          <td>Total selected {{ `(${selectedTechnicians.length})` }}</td>
          <td class="text-center stat-cell">
            <div class="stat-number">
              <a
                @click="
                  openTechnicianRequests(
                    selectedTechnicians.map((tech) => tech.technicianName),
                    'Open',
                    false
                  )
                "
                class="stat-number"
              >
                {{
                  overall.open > 0
                    ? formatNumberWithCommas(
                        selectedTechnicians.reduce(
                          (acc, tech) => acc + tech.open,
                          0
                        )
                      )
                    : "-"
                }}
              </a>
            </div>
            <div class="overdue-number">
              <a
                @click="
                  openTechnicianRequests(
                    selectedTechnicians.map((tech) => tech.technicianName),
                    'Open',
                    true
                  )
                "
                class="overdue-number"
              >
                {{
                  overall.overdueOpen > 0
                    ? formatNumberWithCommas(
                        selectedTechnicians.reduce(
                          (acc, tech) => acc + tech.overdueOpen,
                          0
                        )
                      )
                    : "-"
                }}
              </a>
            </div>
          </td>
          <td class="text-center stat-cell">
            <div class="stat-number">
              <a
                @click="
                  openTechnicianRequests(
                    selectedTechnicians.map((tech) => tech.technicianName),
                    'On Hold',
                    false
                  )
                "
                class="stat-number"
              >
                {{
                  overall.onHold > 0
                    ? formatNumberWithCommas(
                        selectedTechnicians.reduce(
                          (acc, tech) => acc + tech.onHold,
                          0
                        )
                      )
                    : "-"
                }}
              </a>
            </div>
            <div class="overdue-number">
              <a
                @click="
                  openTechnicianRequests(
                    selectedTechnicians.map((tech) => tech.technicianName),
                    'On Hold',
                    true
                  )
                "
                class="overdue-number"
              >
                {{
                  overall.overdueOnHold > 0
                    ? formatNumberWithCommas(
                        selectedTechnicians.reduce(
                          (acc, tech) => acc + tech.overdueOnHold,
                          0
                        )
                      )
                    : "-"
                }}
              </a>
            </div>
          </td>
          <td class="text-center stat-cell">
            <div class="stat-number">
              <a
                @click="
                  openTechnicianRequests(
                    selectedTechnicians.map((tech) => tech.technicianName),
                    'Closed',
                    false
                  )
                "
                class="stat-number"
              >
                {{
                  overall.closed > 0
                    ? formatNumberWithCommas(
                        selectedTechnicians.reduce(
                          (acc, tech) => acc + tech.closed,
                          0
                        )
                      )
                    : "-"
                }}
              </a>
            </div>
          </td>
          <td class="text-center stat-cell">
            <div class="stat-number">
              <a
                Total
                @click="
                  openTechnicianRequests(
                    selectedTechnicians.map((tech) => tech.technicianName),
                    null,
                    false
                  )
                "
                class="stat-number"
              >
                {{
                  overall.totalTickets > 0
                    ? formatNumberWithCommas(
                        selectedTechnicians.reduce(
                          (acc, tech) => acc + tech.total,
                          0
                        )
                      )
                    : "-"
                }}
              </a>
            </div>
            <div class="overdue-number">
              <a
                @click="
                  openTechnicianRequests(
                    selectedTechnicians.map((tech) => tech.technicianName),
                    null,
                    true
                  )
                "
                class="overdue-number"
              >
                {{
                  overall.overdueTotal > 0
                    ? formatNumberWithCommas(
                        selectedTechnicians.reduce(
                          (acc, tech) => acc + tech.overdueTotal,
                          0
                        )
                      )
                    : "-"
                }}
              </a>
            </div>
          </td>
        </tr>
        <tr class="total-row font-weight-bold">
          <td>
            Total All Tickets
          </td>
          <td class="text-center stat-cell">
            <div class="stat-number">
              <a
                @click="openTechnicianRequests(null, 'Open', false)"
                class="stat-number"
              >
                {{
                  overall.open > 0 ? formatNumberWithCommas(overall.open) : "-"
                }}
              </a>
            </div>
            <div class="overdue-number">
              <a
                @click="openTechnicianRequests(null, 'Open', true)"
                class="overdue-number"
              >
                {{
                  overall.overdueOpen > 0
                    ? formatNumberWithCommas(overall.overdueOpen)
                    : "-"
                }}
              </a>
            </div>
          </td>
          <td class="text-center stat-cell">
            <div class="stat-number">
              <a
                @click="openTechnicianRequests(null, 'On Hold', false)"
                class="stat-number"
              >
                {{
                  overall.onHold > 0
                    ? formatNumberWithCommas(overall.onHold)
                    : "-"
                }}
              </a>
            </div>
            <div class="overdue-number">
              <a
                @click="openTechnicianRequests(null, 'On Hold', true)"
                class="overdue-number"
              >
                {{
                  overall.overdueOnHold > 0
                    ? formatNumberWithCommas(overall.overdueOnHold)
                    : "-"
                }}
              </a>
            </div>
          </td>
          <td class="text-center stat-cell">
            <div class="stat-number">
              <a
                @click="openTechnicianRequests(null, 'Closed', false)"
                class="stat-number"
              >
                {{
                  overall.closed > 0
                    ? formatNumberWithCommas(overall.closed)
                    : "- "
                }}
              </a>
            </div>
          </td>
          <td class="text-center stat-cell">
            <div class="stat-number">
              <a
                @click="openTechnicianRequests(null, null, false)"
                class="stat-number"
              >
                {{
                  overall.totalTickets > 0
                    ? formatNumberWithCommas(overall.totalTickets)
                    : "-"
                }}
              </a>
            </div>
            <div class="overdue-number">
              <a
                @click="openTechnicianRequests(null, null, true)"
                class="overdue-number"
              >
                {{
                  overall.overdueTotal > 0
                    ? formatNumberWithCommas(overall.overdueTotal)
                    : "-"
                }}
              </a>
            </div>
          </td>
        </tr>
      </tbody>
    </div>
  </v-card>
</template>

<script>
import { formatNumberWithCommas } from "@/utils/helpers";
export default {
  name: "DashboardSummary",
  data() {
    return {
      loading: false,
      technicians: [],
      selectedTechnicians: [],
      selectedTechnicianNames: [],
      overall: {
        totalTickets: 0,
        open: 0,
        onHold: 0,
        closed: 0,
        overdueOpen: 0,
        overdueOnHold: 0,
        overdueTotal: 0,
      },
      // Filter state
      activeFilterColumn: null, // which column's dropdown is open
      // Pending selection in dropdown
      currentFilter: {
        column: null,
        dateType: null,
        dateSelection: null,
        fromDate: null,
        toDate: null,
      },
      // Applied filter (sent to API, shown in chip)
      appliedFilter: null,
    };
  },
  computed: {
    dateSelectionItems() {
      return [
        "Today",
        "Yesterday",
        "Last 7 Days",
        "Last 30 Days",
        "select range",
      ];
    },
    columnLabels() {
      return {
        open: "Open",
        onHold: "On Hold",
        closed: "Closed",
        total: "Total",
      };
    },
    activeFilterLabel() {
      // Show label only for applied filter
      if (!this.appliedFilter) return null;

      const dateTypeLabel =
        this.appliedFilter.dateType === "createDate"
          ? "Create Date"
          : "Due Date";

      let dateLabel = this.appliedFilter.dateSelection;
      if (this.appliedFilter.dateSelection === "select range") {
        dateLabel = `${this.appliedFilter.fromDate} - ${
          this.appliedFilter.toDate
        }`;
      }

      return `${
        this.columnLabels[this.appliedFilter.column]
      } (${dateTypeLabel}: ${dateLabel})`;
    },

    sortedTechnicians() {
      return this.technicians
        .map((t) => {
          t.technicianName =
            t.technicianName == null ? "Unassigned" : t.technicianName;
          return t;
        })
        .filter((t) => {
          // If no filter, show only technicians with open or onHold tickets
          if (!this.hasFilter) {
            return t.open > 0 || t.onHold > 0;
          }
          return true; // With filter, show all from API
        })
        .sort((a, b) => b.open - a.open);
    },

    selectAllIcon() {
      if (
        this.selectedTechnicianNames.length === this.sortedTechnicians.length
      ) {
        return "mdi-close-box";
      }
      if (this.selectedTechnicianNames.length > 0) {
        return "mdi-minus-box";
      }
      return "mdi-checkbox-blank-outline";
    },

    hasFilter() {
      return !!this.appliedFilter;
    },

    isPendingFilterComplete() {
      return (
        this.currentFilter &&
        this.currentFilter.column &&
        this.currentFilter.dateType &&
        this.currentFilter.dateSelection &&
        this.currentFilter.fromDate &&
        this.currentFilter.toDate
      );
    },
  },
  watch: {
    "currentFilter.toDate": function(newVal) {
      if (
        newVal &&
        this.currentFilter.fromDate &&
        this.currentFilter.dateSelection === "select range" &&
        this.currentFilter.column
      ) {
        this.applyFilter();
      }
    },
    "currentFilter.fromDate": function(newVal) {
      if (
        newVal &&
        this.currentFilter.toDate &&
        this.currentFilter.dateSelection === "select range" &&
        this.currentFilter.column
      ) {
        this.applyFilter();
      }
    },
  },
  async mounted() {
    this.loadFilter();
    await this.loadData();
    this.setFilter();
  },

  methods: {
    async loadData() {
      this.loading = true;

      try {
        let url = "api/manageengine/GetDashboardSummary";
        if (this.appliedFilter) {
          const dateTypeCode =
            this.appliedFilter.dateType === "createDate" ? "c" : "d";

          var status = this.appliedFilter.column;
          if (status == "onHold") {
            status = "On Hold";
          }
          const filterParam = `${status},${dateTypeCode},${
            this.appliedFilter.fromDate
          },${this.appliedFilter.toDate}`;
          url += `?filter=${filterParam}`;
        }

        const response = await this.$http.get(url);
        this.technicians = response.technicians;
        this.overall = response.overall;
      } catch (e) {
        console.error("Failed to load dashboard summary:", e);
        this.$notify({
          text: "Failed to load dashboard summary",
          group: "notification",
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    toggleFilterDropdown(column) {
      // If selecting different column, reset filter
      if (this.activeFilterColumn !== column) {
        this.currentFilter = {
          column: column,
          dateType: null,
          dateSelection: null,
          fromDate: null,
          toDate: null,
        };
      }

      this.activeFilterColumn =
        this.activeFilterColumn === column ? null : column;
    },

    async clearFilter() {
      this.appliedFilter = null;
      this.currentFilter = {
        column: null,
        dateType: null,
        dateSelection: null,
        fromDate: null,
        toDate: null,
      };
      localStorage.setItem("dashboard-summary-selected-technician-names", []);
      this.saveFilter();
      await this.loadData();
      this.setFilter();
    },

    onDateTypeChange() {
      this.currentFilter.dateSelection = null;
      this.currentFilter.fromDate = null;
      this.currentFilter.toDate = null;
      localStorage.setItem("dashboard-summary-selected-technician-names", []);
    },

    async onDateSelectionChanged() {
      const today = new Date().toISOString().split("T")[0];

      if (this.currentFilter.dateSelection === "Today") {
        this.currentFilter.fromDate = today;
        this.currentFilter.toDate = today;
      } else if (this.currentFilter.dateSelection === "Yesterday") {
        const yesterday = new Date(new Date().setDate(new Date().getDate() - 1))
          .toISOString()
          .split("T")[0];
        this.currentFilter.fromDate = yesterday;
        this.currentFilter.toDate = yesterday;
      } else if (this.currentFilter.dateSelection === "Last 7 Days") {
        this.currentFilter.fromDate = new Date(
          new Date().setDate(new Date().getDate() - 7)
        )
          .toISOString()
          .split("T")[0];
        this.currentFilter.toDate = today;
      } else if (this.currentFilter.dateSelection === "Last 30 Days") {
        this.currentFilter.fromDate = new Date(
          new Date().setDate(new Date().getDate() - 30)
        )
          .toISOString()
          .split("T")[0];
        this.currentFilter.toDate = today;
      } else if (this.currentFilter.dateSelection === "select range") {
        this.currentFilter.fromDate = null;
        this.currentFilter.toDate = null;
        return;
      }

      // Apply filter when dates are set
      await this.applyFilter();
    },

    closeDropdown() {
      this.activeFilterColumn = null;
    },

    formatNumberWithCommas(number) {
      return formatNumberWithCommas(number);
    },

    openTechnicianRequests(technicianNames, status, filterOverdue) {
      let technicianNamesString = Array.isArray(technicianNames)
        ? JSON.stringify(technicianNames)
        : technicianNames
        ? JSON.stringify([technicianNames])
        : "";

      localStorage.setItem("status-filter-tech", status);
      localStorage.setItem("technicians-filter-tech", technicianNamesString);
      localStorage.setItem("filter-overdue-tech", filterOverdue);

      window.open(
        this.$router.resolve({
          name: "HelpDeskTechnicianRequests",
        }).href,
        "_blank"
      );
    },

    setFilter() {
      var selectedTechnicianNames = localStorage.getItem(
        "dashboard-summary-selected-technician-names"
      );
      if (!selectedTechnicianNames) {
        this.selectedTechnicians = this.sortedTechnicians;
        return;
      }
      if (selectedTechnicianNames) {
        selectedTechnicianNames = JSON.parse(selectedTechnicianNames);
      }

      this.selectedTechnicianNames = selectedTechnicianNames;

      if (selectedTechnicianNames.length > 0) {
        this.selectedTechnicians = this.sortedTechnicians.filter((t) =>
          selectedTechnicianNames.includes(t.technicianName)
        );
      } else {
        this.selectedTechnicians = [];
      }
    },

    saveFilter() {
      if (this.appliedFilter) {
        localStorage.setItem(
          "dashboard-summary-filter",
          JSON.stringify(this.appliedFilter)
        );
        localStorage.setItem(
          "dashboard-summary-filter-requests-show",
          JSON.stringify(this.appliedFilter)
        );
      } else {
        localStorage.removeItem("dashboard-summary-filter");
      }
    },

    loadFilter() {
      const saved = localStorage.getItem("dashboard-summary-filter");
      if (saved) {
        try {
          this.appliedFilter = JSON.parse(saved);
          // Also populate currentFilter so dropdown shows the applied values
          this.currentFilter = { ...this.appliedFilter };
        } catch (e) {
          console.error("Failed to load saved filter:", e);
        }
      }
    },

    async applyFilter() {
      if (this.isPendingFilterComplete) {
        // Copy pending filter to applied filter
        this.appliedFilter = { ...this.currentFilter };
        await this.loadData();
        this.setFilter();
        this.saveFilter();
        this.closeDropdown();
      }
    },

    onTechnicianSelected(v) {
      localStorage.setItem(
        "dashboard-summary-selected-technician-names",
        JSON.stringify(v)
      );
      this.setFilter();
    },

    toggleSelectAll() {
      if (
        this.selectedTechnicianNames.length == this.sortedTechnicians.length
      ) {
        this.selectedTechnicianNames = [];
      } else {
        this.selectedTechnicianNames = this.sortedTechnicians.map(
          (t) => t.technicianName
        );
      }
      this.onTechnicianSelected(this.selectedTechnicianNames);
    },
  },
};
</script>

<style scoped>
.summary-table {
  width: 100%;
  table-layout: fixed;
}

.summary-table th {
  background-color: #f5f5f5;
  font-weight: 600;
  color: #666;
  font-size: 13px;
  padding: 8px 16px !important;
}

.summary-table th:first-child {
  width: 400px; /* Technician column */
}

.summary-table th:not(:first-child) {
  min-width: 150px; /* Status columns */
}

.summary-table td {
  padding: 8px 16px !important;
  border-bottom: 1px solid #eee;
}

.stat-cell {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

.stat-number {
  font-size: 18px;
  font-weight: 500;
  line-height: 1.2;
}

.overdue-number {
  color: #f44336;
  font-size: 14px;
  font-weight: 600;
  margin-top: 7px;
  line-height: 1;
}

.total-row {
  background-color: #fafafa;
}

.total-row td {
  border-top: 2px solid #ddd;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.total-row .stat-number {
  font-size: 18px;
}

.total-row .overdue-number {
  font-size: 14px;
}
.select-all-item {
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.select-all-item:hover {
  background-color: #f5f5f5;
}

.cursor-pointer:hover {
  cursor: pointer;
}

.position-relative {
  position: relative;
}

.date-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 10;
  min-width: 180px;
}

.dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  background: transparent;
}
</style>
